import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IonSkeletonText, IonButton, IonPopover } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import usageService from 'services/usage';
import { accountApi, ACCOUNT_ENDPOINTS } from 'services/api';
import './UsagePage.css';

const EXECUTION_TYPES = [
  { key: 'all', label: 'All' },
  { key: 'analysis', label: 'Analysis' },
  { key: 'test', label: 'Test Run' },
  { key: 'workflow', label: 'Workflow' },
];

const TYPE_LABELS = EXECUTION_TYPES.reduce((acc, t) => {
  if (t.key !== 'all') acc[t.key] = t.label;
  return acc;
}, {});

const EXECUTION_ROUTES = {
  analysis: (exec) => `/projects/${exec.projectId}/analysis/${exec.id}`,
  test: (exec) => `/projects/${exec.projectId}/runs/${exec.id}`,
  ticket_implementation: (exec) => `/projects/${exec.projectId}/implementations/${exec.id}`,
};

const UsagePage = () => {
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('30d');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [dailyTestUploadData, setDailyTestUploadData] = useState(null);
  const [dailyWorkflowData, setDailyWorkflowData] = useState(null);

  const [deleting, setDeleting] = useState(false);

  const loadUsage = useCallback(async () => {
    try {
      setLoading(true);
      const data = await usageService.getUsage();
      setUsageData(data);
    } catch (error) {
      console.error('Error loading usage:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClearAllData = async () => {
    if (!window.confirm(
      'Clear all test run artifacts?\n\n' +
      'This will delete:\n' +
      '- All test execution videos and recordings\n' +
      '- All test results and event data from S3\n\n' +
      'Execution history will be preserved.\n' +
      'This action cannot be undone.'
    )) {
      return;
    }

    try {
      setDeleting(true);
      const result = await accountApi.delete(ACCOUNT_ENDPOINTS.EXECUTIONS_BULK_DELETE);
      await loadUsage();
      alert(`Cleared artifacts for ${result.deletedCount} test runs. Storage freed: ${(result.deletedBytes / 1024 / 1024).toFixed(2)} MB`);
    } catch (error) {
      console.error('Error clearing artifacts:', error);
      alert('Failed to clear artifacts: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const convertToChartData = useCallback((items) => {
    const days = [];
    const today = new Date();
    const dailyMap = {};

    for (const item of items) {
      const dateKey = new Date(item.date).toISOString().slice(0, 10);
      dailyMap[dateKey] = (dailyMap[dateKey] || 0) + 1;
    }

    for (let i = 14; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().slice(0, 10);

      days.push({
        date: i === 14 || i === 0
          ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : '',
        count: dailyMap[dateKey] || 0,
        fullDate: date.toLocaleDateString(),
      });
    }

    return days;
  }, []);

  useEffect(() => {
    if (usageData?.testUploads) {
      setDailyTestUploadData(convertToChartData(usageData.testUploads));
    }
    if (usageData?.workflowExecutions) {
      setDailyWorkflowData(convertToChartData(usageData.workflowExecutions));
    }
  }, [usageData, convertToChartData]);

  const allExecutions = useMemo(() => {
    if (!usageData) return [];
    const analyses = (usageData.analyses || []).map(a => ({ ...a, type: 'analysis' }));
    const tests = (usageData.testUploads || []).map(t => ({ ...t, type: 'test' }));
    const impls = (usageData.implementations || []).map(i => ({ ...i, type: 'ticket_implementation' }));
    const workflows = (usageData.workflowExecutions || []).map(w => ({ ...w, type: 'workflow' }));
    return [...analyses, ...tests, ...impls, ...workflows].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [usageData]);

  const getFilteredExecutions = useCallback(() => {
    const now = new Date();
    let cutoff;

    switch (filter) {
      case '1d':
        cutoff = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
        break;
      case '7d':
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
      default:
        cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
    }

    return allExecutions.filter(exec => {
      if (new Date(exec.date) < cutoff) return false;
      if (typeFilter !== 'all' && exec.type !== typeFilter) return false;
      return true;
    });
  }, [allExecutions, filter, typeFilter]);

  const testUploadsUsed = usageData?.testUploadsUsed || 0;
  const testUploadsLimit = usageData?.limits?.videoVerifications || 100;
  const workflowExecutionsUsed = usageData?.workflowExecutionsUsed || 0;
  const workflowExecutionsLimit = usageData?.limits?.workflowExecutions || 25;
  const storageUsedBytes = usageData?.storageUsedBytes || 0;
  const storageLimitBytes = usageData?.storageLimitBytes || (1 * 1024 * 1024 * 1024);
  const periodEnd = usageData?.periodEnd ? new Date(usageData.periodEnd) : null;
  const periodStart = usageData?.periodStart ? new Date(usageData.periodStart) : null;
  
  // Check if user has a real Stripe subscription (paid plan with actual billing cycle)
  const hasStripeSubscription = usageData?.plan && usageData.plan !== 'open_source' && periodStart && periodEnd;
  const isRollingWindow = !hasStripeSubscription;

  const formatStorage = (bytes) => {
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(2)} KB`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatModel = (exec) => {
    const agent = exec.agentType;
    const model = exec.model;
    if (agent && model) return `${agent} / ${model}`;
    if (agent) return agent;
    if (model) return model;
    return '-';
  };

  const filteredExecutions = getFilteredExecutions();
  const maxTestUploadBarCount = dailyTestUploadData ? Math.max(...dailyTestUploadData.map(d => d.count), 1) : 1;
  const maxWorkflowBarCount = dailyWorkflowData ? Math.max(...dailyWorkflowData.map(d => d.count), 1) : 1;

  const handleExportCSV = () => {
    if (!filteredExecutions.length) return;
    const headers = ['Date', 'Type', 'Project', 'Status', 'Model'];
    const rows = filteredExecutions.map(exec => [
      new Date(exec.date).toISOString(),
      TYPE_LABELS[exec.type] || exec.type,
      exec.projectName,
      exec.status,
      formatModel(exec),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zibby-usage-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Usage - Zibby</title>
        <meta name="description" content="View your Zibby usage and execution history." />
      </Helmet>
      <div className="usage-page page-layout main-body">
        <div className="page-header-center">
          <h1>Usage</h1>
          <p>Monitor your workflow execution usage and limits</p>
        </div>

        <div className="page-card-content">
          {/* Usage Chart Card */}
          <div className="page-card">
            <div className="card-header">
              <h2>Usage</h2>
              {periodEnd && (
                <span className="usage-reset-label">
                  {isRollingWindow 
                    ? 'Rolling 30-day limit' 
                    : `Resets ${periodEnd.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  }
                </span>
              )}
            </div>

            <div className="usage-chart-dual-container">
              {/* Cloud Workflow Executions Chart */}
              <div className="usage-chart-container">
                <div className="usage-total">
                  <div className="total-label">Cloud Workflow Executions</div>
                  {loading ? (
                    <IonSkeletonText animated style={{ width: '120px', height: '24px' }} />
                  ) : (
                    <div className="total-amount">{workflowExecutionsUsed} / {workflowExecutionsLimit}</div>
                  )}
                </div>

                <div className="usage-chart">
                  <div className="chart-bars">
                    {dailyWorkflowData && dailyWorkflowData.map((day, index) => (
                      <div key={index} className="chart-bar-container">
                        {day.count > 0 && (
                          <div className="chart-bar-label">{day.count}</div>
                        )}
                        <div
                          className="chart-bar"
                          style={{
                            height: `${(day.count / maxWorkflowBarCount) * 100}%`
                          }}
                          onMouseEnter={(e) => {
                            setHoveredBar({
                              type: 'workflows',
                              date: day.fullDate,
                              count: day.count,
                            });
                            const rect = e.target.getBoundingClientRect();
                            setTooltipPosition({
                              x: rect.left + rect.width / 2,
                              y: rect.top - 10,
                            });
                          }}
                          onMouseLeave={() => setHoveredBar(null)}
                        />
                        {index === 0 && <div className="chart-date">{day.date}</div>}
                        {index === dailyWorkflowData.length - 1 && <div className="chart-date">{day.date}</div>}
                      </div>
                    ))}
                  </div>
                  <div className="chart-baseline"></div>
                </div>
              </div>

              {/* Test Runs Chart */}
              <div className="usage-chart-container">
                <div className="usage-total">
                  <div className="total-label">Test runs</div>
                  {loading ? (
                    <IonSkeletonText animated style={{ width: '120px', height: '24px' }} />
                  ) : (
                    <div className="total-amount">{testUploadsUsed} / {testUploadsLimit}</div>
                  )}
                </div>

                <div className="usage-chart">
                  <div className="chart-bars">
                    {dailyTestUploadData && dailyTestUploadData.map((day, index) => (
                      <div key={index} className="chart-bar-container">
                        {day.count > 0 && (
                          <div className="chart-bar-label">{day.count}</div>
                        )}
                        <div
                          className="chart-bar chart-bar-secondary"
                          style={{
                            height: `${(day.count / maxTestUploadBarCount) * 100}%`
                          }}
                          onMouseEnter={(e) => {
                            setHoveredBar({
                              type: 'tests',
                              date: day.fullDate,
                              count: day.count,
                            });
                            const rect = e.target.getBoundingClientRect();
                            setTooltipPosition({
                              x: rect.left + rect.width / 2,
                              y: rect.top - 10,
                            });
                          }}
                          onMouseLeave={() => setHoveredBar(null)}
                        />
                        {index === 0 && <div className="chart-date">{day.date}</div>}
                        {index === dailyTestUploadData.length - 1 && <div className="chart-date">{day.date}</div>}
                      </div>
                    ))}
                  </div>
                  <div className="chart-baseline"></div>
                </div>
              </div>
            </div>

            {/* Storage Usage Card */}
            <div className="usage-storage-card">
              <div className="storage-header">
                <div className="total-label">Storage Used</div>
                {loading ? (
                  <IonSkeletonText animated style={{ width: '150px', height: '24px' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="total-amount">{formatStorage(storageUsedBytes)} / {formatStorage(storageLimitBytes)}</div>
                    {storageUsedBytes > 0 && (
                      <IonButton
                        size="small"
                        color="danger"
                        fill="outline"
                        onClick={handleClearAllData}
                        disabled={deleting}
                        style={{ fontSize: '12px', height: '28px' }}
                        title="Delete all test runs (zibby test) - analysis executions not affected"
                      >
                        {deleting ? 'Clearing...' : 'Clear Artifacts'}
                      </IonButton>
                    )}
                  </div>
                )}
              </div>
              <div className="storage-bar-container">
                <div 
                  className="storage-bar"
                  style={{ 
                    width: storageUsedBytes > 0 
                      ? `${Math.max((storageUsedBytes / storageLimitBytes) * 100, 0.5)}%`
                      : '0%'
                  }}
                />
              </div>
              <div className="storage-percentage">
                {(() => {
                  const percentage = (storageUsedBytes / storageLimitBytes) * 100;
                  if (storageUsedBytes === 0) return '0.0% used';
                  if (percentage < 0.1) return '< 0.1% used';
                  return `${percentage.toFixed(1)}% used`;
                })()}
              </div>
            </div>

            {hoveredBar && (
              <div
                className="chart-tooltip"
                style={{
                  position: 'fixed',
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y}px`,
                  transform: 'translate(-50%, -100%)',
                  zIndex: 1000,
                }}
              >
                <div className="tooltip-content">
                  <div className="tooltip-date">{hoveredBar.date}</div>
                  <div className="tooltip-row">
                    <div className={`tooltip-indicator ${hoveredBar.type === 'tests' ? 'secondary' : 'workflow'}`}></div>
                    <span className="tooltip-label">
                      {hoveredBar.type === 'tests' ? 'tests' : 'workflows'}
                    </span>
                    <span className="tooltip-value">{hoveredBar.count}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Executions Table */}
          <div className="usage-table-card">
            <div className="usage-table-header">
              <div className="usage-filter-row" style={{ padding: 0, border: 'none' }}>
                {['1d', '7d', '30d'].map(f => (
                  <button
                    key={f}
                    className={`usage-filter-btn ${filter === f ? 'active' : ''}`}
                    onClick={() => setFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="usage-table-header-right">
                <button 
                  id="type-filter-trigger"
                  className="usage-export-btn"
                  onClick={() => setIsTypeFilterOpen(true)}
                >
                  {EXECUTION_TYPES.find(t => t.key === typeFilter)?.label || 'All'}
                </button>
                <IonPopover
                  trigger="type-filter-trigger"
                  isOpen={isTypeFilterOpen}
                  onDidDismiss={() => setIsTypeFilterOpen(false)}
                  showBackdrop={false}
                >
                  <div className="usage-type-filter-popover">
                    {EXECUTION_TYPES.map(t => (
                      <div
                        key={t.key}
                        className={`menu-item ${typeFilter === t.key ? 'active' : ''}`}
                        onClick={() => {
                          setTypeFilter(t.key);
                          setIsTypeFilterOpen(false);
                        }}
                      >
                        {t.label}
                      </div>
                    ))}
                  </div>
                </IonPopover>
                <button className="usage-export-btn" onClick={handleExportCSV}>
                  Export CSV
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: '0' }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="usage-skeleton-row">
                    <IonSkeletonText animated style={{ width: '120px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '80px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '100px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '80px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '140px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '60px', height: '16px' }} />
                  </div>
                ))}
              </div>
            ) : filteredExecutions.length > 0 ? (
              <table className="usage-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Project</th>
                    <th>Status</th>
                    <th>Model</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExecutions.map((exec) => {
                    const routeFn = EXECUTION_ROUTES[exec.type];
                    return (
                      <tr
                        key={exec.id}
                        className={routeFn ? 'clickable-row' : ''}
                        onClick={() => routeFn && navigate(routeFn(exec))}
                      >
                        <td className="col-date">{formatDate(exec.date)}</td>
                        <td className="col-project">{TYPE_LABELS[exec.type] || exec.type}</td>
                        <td className="col-project">{exec.projectName}</td>
                        <td>
                          <span className="col-status">
                            <span className={`status-dot ${exec.status}`} />
                            {exec.status}
                          </span>
                        </td>
                        <td className="col-project">{formatModel(exec)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="usage-empty-state">
                <p>No executions found for this period</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UsagePage;
